import { useState } from 'react';
import { Lightbulb, Target, X } from 'lucide-react';
import type { KnowledgeGraph } from '@lizrd/contracts';
import { Card } from './ui/card';
import { costOf, usd } from '../lib/graph-visuals';

const STORAGE_KEY = 'lizrd.explorer-guide.collapsed';

// The three-step mental model of the Explorer. Each step scrolls to its section.
const STEPS = [
  {
    id: 'explorer-overview',
    title: 'See what you run',
    body: 'Start here for the full picture of everything Lizrd has discovered across your code and cloud — every resource in the domain, how it is composed, and the total you spend each month.',
  },
  {
    id: 'explorer-cost',
    title: 'Find where the money goes',
    body: 'Follow the money next: break your spend down by cloud and category, or switch to the tree map to spot at a glance the handful of resources that make up most of the bill.',
  },
  {
    id: 'explorer-drivers',
    title: 'Understand why',
    body: 'Then the payoff — open any major cost driver to read, in plain language, what it actually is, how hard it is working, and which product capabilities it serves.',
  },
] as const;

function scrollToSection(id: string) {
  document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
}

/**
 * The Explorer's "how to read this page" guide: the three-step mental model
 * (overview → cost → why), framed around the payoff — walking into a cost
 * meeting able to name your biggest drivers and why they exist in ten seconds.
 * Dismissible (persisted), with a live pointer to the current top cost driver.
 */
export function ExplorerGuide({
  graph,
  onSeeWhy,
}: {
  graph?: KnowledgeGraph;
  onSeeWhy?: (nodeId: string) => void;
}) {
  const [collapsed, setCollapsed] = useState(() => {
    try {
      return localStorage.getItem(STORAGE_KEY) === '1';
    } catch {
      return false;
    }
  });
  const persist = (value: boolean) => {
    setCollapsed(value);
    try {
      localStorage.setItem(STORAGE_KEY, value ? '1' : '0');
    } catch {
      /* private mode — dismissal just won't persist */
    }
  };

  const top = graph?.nodes
    .filter((n) => costOf(n.properties) > 0)
    .sort((a, b) => costOf(b.properties) - costOf(a.properties))[0];

  if (collapsed) {
    return (
      <button
        onClick={() => persist(false)}
        className="flex w-full items-center gap-2 rounded-md border border-hairline bg-surface-2/40 px-4 py-2 text-left text-xs transition-colors hover:bg-surface-2"
      >
        <Lightbulb aria-hidden className="h-4 w-4 text-iris" />
        <span className="font-semibold text-haze">How to read this page</span>
        <span className="ml-auto text-muted">Show ▸</span>
      </button>
    );
  }

  return (
    <Card className="relative space-y-4 p-5">
      <button
        onClick={() => persist(true)}
        aria-label="Hide guide"
        className="absolute right-3 top-3 grid h-6 w-6 place-items-center rounded-sm border border-border text-muted transition-colors hover:bg-surface-2 hover:text-bone"
      >
        <X aria-hidden className="h-4 w-4" />
      </button>

      <div className="space-y-1 pr-8">
        <div className="text-[0.65rem] font-semibold uppercase tracking-wide text-iris">
          How to read this page
        </div>
        <h2 className="font-display text-lg font-bold text-bone">
          Your Infrastructure, Explained in Three Steps
        </h2>
        <p className="text-sm text-haze">
          By the end of this page you can say, in ten seconds:{' '}
          <span className="text-bone">
            “here are our biggest cost drivers — and here’s why they exist.”
          </span>
        </p>
      </div>

      <ol className="grid gap-4 sm:grid-cols-3">
        {STEPS.map((step, i) => (
          <li key={step.id}>
            <button
              onClick={() => scrollToSection(step.id)}
              className="group flex h-full w-full flex-col rounded-md p-3 text-left transition-colors hover:bg-surface-2"
            >
              <div className="font-mono text-2xl font-bold leading-none text-iris tnum">
                {String(i + 1).padStart(2, '0')}
              </div>
              <h3 className="mt-2.5 font-semibold text-bone">{step.title}</h3>
              <p className="mt-1.5 text-xs leading-snug text-muted">{step.body}</p>
              <span className="mt-3 text-[0.65rem] text-viridian opacity-0 transition-opacity group-hover:opacity-100">
                Jump to this section ↓
              </span>
            </button>
          </li>
        ))}
      </ol>

      <div className="flex flex-wrap items-center gap-x-2 gap-y-1 rounded-md border border-iris/25 bg-iris/5 px-3 py-2 text-sm">
        <Target aria-hidden className="h-4 w-4 flex-none text-iris" />
        {top ? (
          <>
            <span className="text-haze">Your biggest cost driver right now:</span>
            <span className="font-semibold text-bone">{top.name}</span>
            <span className="font-display text-iris">
              {usd(costOf(top.properties))}
              <span className="text-xs text-muted">/mo</span>
            </span>
            <button
              onClick={() => (onSeeWhy ? onSeeWhy(top.id) : scrollToSection('explorer-drivers'))}
              className="ml-auto text-xs text-viridian hover:underline"
            >
              See why ↓
            </button>
          </>
        ) : (
          <span className="text-muted">
            Once your first sync completes, your biggest cost driver will appear here.
          </span>
        )}
      </div>
    </Card>
  );
}
